<template>
  <fieldset :class="{ 'app-embedded-fieldset': props.embedded }"><!-- using fieldset as verification code was expected in future release -->
    <legend>
      <span :class=" isRequired ?  'app-required-indicator __after' : '' ">{{ props.titleEmail }}</span>
    </legend>
    <div class="app-contact-inputs">
      <ValidationForm ref="emailForm" >
        <Field name="email address" v-slot="{ field, errors }" rules="email|max:80">
          <div class="app-input-group app-contact-info" :class="{ 'app-invalid-group': errors.length > 0 || invalidEmailChars }"
               ref="emailGroup">
            <label for="userEmail" class="sr-only">Enter your email address</label>
            <input class="app-user-email" 
                  v-bind="field" 
                  id="userEmail" 
                  :ref="userEmail" 
                  type="email"
                  :value="myEmail"
                  v-model="myEmail"               
                  @blur="checkEmail"
                  :readonly="editable"
                  :disabled="editable">
            <p v-if="invalidEmailChars" ref="invalidChar">Email address contains invalid characters.</p>
            <p v-else ref="invalidEmail">{{ errors[0] }}</p>
          </div>
        </Field>
      </ValidationForm>
    </div>
  </fieldset>
</template>


<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useStore } from 'vuex'; 
import { max as maxRule, email as emailRule } from '@vee-validate/rules'; 
import { defineRule, Form as ValidationForm, Field  } from 'vee-validate';


defineRule('email', (value, _, ctx) => {
  const valid = emailRule(value);
  if (!valid) {
    return `Please enter a valid ${ctx.field}.`
  }
  return true;
});

defineRule('max', (value, [limit], ctx) => {
  const isValid = maxRule(value, [limit]);
  if (!isValid) {
    return `${ctx.field} is limited to ${limit} characters.`
  }
  return true;
});

const props = defineProps({
  embedded: {
    type: Boolean,
    required: false,
    default: false,
  },
  isModal: {
    type: Boolean,
    required: false,
    default: false,
  },
  verify: {
    type: Boolean,
    required: false,
    default: false,
  },
  editable: {
    type: Boolean,
    required: false,
    default: false,
  },
  titleEmail: {
    type: String,
    required: false,
    default: 'Email',
  },
  userEmail: {
    type: String,
    required: false,
    default: 'userEmail',
  },
  isRequired: {
    type: Boolean,
    required: false,
    default: true,
  }
});

const emit = defineEmits(['doResetVerify', 'doEmail']);

const store = useStore(); // Access the Vuex store
const myEmail = ref('');
const emailForm = ref(null);

// Computed properties
const invalidEmailChars = computed(() => {
  const pattern = /^[a-zA-Z0-9@._-]*$/;
  return !pattern.test(myEmail.value);
});

const exposedUserEmail = computed(() => props.userEmail);

watch(myEmail, () => {
  checkEmail();
});

watch(() => props.verify, (newVal) => {
  if (newVal) {
    checkEmail();
  }
});

onMounted(() => {
  if (store.state.guestLogin.finalizationCart && store.state.guestLogin.finalizationCart.finalizeCartResponse.transactionCart) {
    const info = store.state.guestLogin.finalizationCart.finalizeCartResponse.transactionCart;
    if (info && info.email && info.email.length > 0) {
      myEmail.value = info.email;
    }
  }
});

const checkEmail = async () => {
  const { valid } = await emailForm.value.validate();
  const hasError = !valid || (myEmail.value.length > 0 && invalidEmailChars.value);

  if (hasError) {
    emit('doResetVerify');
  }

  // isValid is only used by ModalContact (not UserContact)
  emit('doEmail', { email: myEmail.value, isValid: !hasError });
};

defineExpose({
  userEmail: exposedUserEmail,
  myEmail,
  checkEmail,
  invalidEmailChars, 
});
</script>


<style lang="scss" scoped>
@import "~@ridmv/web-themes/styles/contact";

.app-input-group {
  p {
    padding-bottom: 0em !important;
    padding-top: 0em !important;
  }
}
</style>
